import { ZERO } from "shared/base-components/number-spinner.component";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BmarkOrderData } from "../BmarkOrderData.model";
import { BmarkRiskData } from "../BmarkRiskData.model";

/**
 * Type for a single risk in a BM order
 */
export type RiskData = {
  /** Risk ID */
  riskId: string;
  /** True is this is Direct/Prime risk */
  isDealt: boolean;
  /** Allocation list for the risk */
  allocations: CubiqTS.AllocationDataImpl[];
};

/**
 * Type representing source and dest for Risk Aggregation for single set of allocs
 */
export type RiskAggregate = {
  /** Allocations to be aggregated */
  allocations: CubiqTS.AllocationDataImpl[];

  // Aggregation Results
  clientIntent?: CubiqTS.OrderIntent;
  // Aggregate amounts for the risk - always positive
  riskAmount?: CubiqTS.BigNumber;
  usdAmount?: CubiqTS.BigNumber;
  counterAmount?: CubiqTS.BigNumber;
  filledBaseAmount?: CubiqTS.BigNumber;
  /** Number of allocation Buys */
  buyCount?: number;
  /** Number of Allocation Sells */
  sellCount?: number;

  fixedCcy: string;
  isSSP?: boolean;
  isSwap?: boolean;
  swapNearAlloc?: CubiqTS.AllocationDataImpl;
  swapFarAlloc?: CubiqTS.AllocationDataImpl;

  useNPV?: boolean;
  npvIntent?: CubiqTS.OrderIntent;
  npvRiskAmount?: CubiqTS.BigNumber;
  npvCounterAmount?: CubiqTS.BigNumber;
  npvFilledBaseAmount?: CubiqTS.BigNumber;
  npvRiskAmountSigned?: CubiqTS.BigNumber;
};

export class BMRiskUtils {
  public static systemUseNPV: boolean = false;

  /**
   * Partitions an array of allocations and returns a Map of risk Ids to allocations for that risk.
   *
   * @param allocations
   * @returns
   */
  public static createRiskMap(allocations: CubiqTS.AllocationDataImpl[]): Map<string, RiskData> {
    const riskMap = new Map<string, RiskData>();
    if (Array.isArray(allocations)) {
      // First split all allocations into their separate risks
      allocations.forEach((allocation) => {
        // Add top-level and component allocations
        BMRiskUtils.addAllocationToRiskData(riskMap, allocation);
      });
    }
    return riskMap;
  }

  /**
   * Agggregate all allocations deriving total amounts, intent etc
   * Use this instead of aggregate when no RiskAggregate model is available or needed
   * @param allocations
   * @returns
   */
  public static aggregateAllocations(
    fixedCCY: string,
    product: CubiqTS.Product,
    allocations: CubiqTS.AllocationDataImpl[],
  ): RiskAggregate {
    const retAggregate: RiskAggregate = {
      fixedCcy: fixedCCY,
      isSwap: CubiqTS.Product.SWP == product,
      isSSP: CubiqTS.Product.SSP == product,
      allocations,
    };

    this.aggregate(retAggregate);
    return retAggregate;
  }

  /**
   * Add a single allocation to the RiskMap
   * @param riskMap
   * @param allocation
   */
  private static addAllocationToRiskData(
    riskMap: Map<string, RiskData>,
    allocation: CubiqTS.AllocationDataImpl,
  ) {
    // In case a BMALgo somehow manages to get through that doesn't have a risk ID
    const riskId =
      allocation.riskId?.toString() ??
      (allocation.allocationId != null
        ? allocation.allocationId.toString()
        : allocation.allocationRef);

    let riskData = riskMap.get(riskId);
    const isDealt =
      allocation.allocationType == CubiqTS.AllocationType.CROSS ||
      allocation.allocationType == CubiqTS.AllocationType.PRIME;
    if (!riskData) {
      riskData = { riskId, isDealt, allocations: [] };
      riskMap.set(riskId, riskData);
    }
    riskData.allocations.push(allocation);
    if (allocation.componentAllocations && allocation.componentAllocations.length) {
      allocation.componentAllocations.forEach((componentAallocation) =>
        BMRiskUtils.addAllocationToRiskData(riskMap, componentAallocation),
      );
    }
  }

  /**
   * Function used by both Risk and SwapLeg to calculate the risk aggregates for bunch of
   * allocations
   * Use this instead of aggregate(allocations) when there islready a model for RIskAggregate that
   * should be updated e.g. SwapLeg, BMRiskData
   */
  public static aggregate(riskAggregate: RiskAggregate) {
    let riskValue = new CubiqTS.BigNumber(0);
    let counterValue = new CubiqTS.BigNumber(0);
    let filledBaseValue = new CubiqTS.BigNumber(0);

    let npvRiskValue = new CubiqTS.BigNumber(0);
    let npvCounterValue = new CubiqTS.BigNumber(0);
    let npvFilledBaseValue = new CubiqTS.BigNumber(0);

    let buyValue = 0;
    let sellValue = 0;

    const allocations = riskAggregate.allocations;

    riskAggregate.useNPV = true;

    // For the moment, each allocation carried the full risk amount so
    let usdValue = new CubiqTS.BigNumber(0);
    usdValue = allocations[0].usdEquivQty;

    if (allocations && allocations.length) {
      for (const allocation of allocations) {
        const baseIsFixed = allocation.baseCcy == allocation.fixedCcy;

        let leavesQty = allocation.leavesQty;
        let allocationQty =
          null != allocation.spotAllocationQty
            ? allocation.spotAllocationQty
            : allocation.allocationQty;
        let allocCounterAmt: CubiqTS.BigNumber =
          null != allocation.spotSettleQty ? allocation.spotSettleQty : allocation.settleQty;

        let leavesSettleQty = allocation.leavesSettleQty;
        let settleQty = allocation.leavesSettleQty;

        let npvAllocationQty = allocation.npvAllocationQty;
        let npvSettleQty = allocation.npvSettleQty;

        if (!BMRiskUtils.systemUseNPV || npvAllocationQty == null || npvSettleQty == null) {
          npvAllocationQty = allocationQty;
          npvSettleQty = allocCounterAmt;
          riskAggregate.useNPV = false;
        }

        let isHedgeOrder: boolean = false;

        // In case of a BM Algo or BMHO use the order values instead
        if (riskAggregate instanceof BmarkRiskData) {
          let riskData = riskAggregate as BmarkRiskData;
          if (
            riskData.orderData?.orderType === CubiqTS.OrderType.BMALGO ||
            (riskData.orderData?.orderType === CubiqTS.OrderType.BENCHMARK &&
              riskData.orderData?.fixingDetails?.bmOrderType ===
                CubiqTS.BenchmarkOrderType.PositionOrder)
          ) {
            let orderData = riskData.orderData;
            leavesQty = orderData.leavesQty;
            allocationQty = orderData.orderQty;
            leavesSettleQty = orderData.leavesSettleQty;
            settleQty = orderData.settleQty;
            npvAllocationQty = allocationQty;
            npvSettleQty = allocCounterAmt;
            isHedgeOrder = true;
          }
        }

        const fillValue = baseIsFixed
          ? leavesQty != null
            ? allocationQty.minus(leavesQty)
            : allocationQty
          : leavesSettleQty != null
          ? settleQty.minus(leavesSettleQty)
          : settleQty;

        let npvFillValue = fillValue;

        if (!isHedgeOrder) {
          if (baseIsFixed) {
            npvFillValue =
              leavesQty == null || !leavesQty.isZero()
                ? npvAllocationQty
                : new CubiqTS.BigNumber(0);
          } else {
            npvFillValue =
              leavesSettleQty == null || !leavesSettleQty.isZero()
                ? npvCounterValue
                : new CubiqTS.BigNumber(0);
          }
        }

        if (allocation.allocationIntent == CubiqTS.OrderIntent.BUY) {
          buyValue++;
          riskValue = allocationQty != null ? riskValue.plus(allocationQty) : riskValue;
          counterValue =
            allocCounterAmt != null ? counterValue.plus(allocCounterAmt) : counterValue;
          filledBaseValue = fillValue != null ? filledBaseValue.plus(fillValue) : filledBaseValue;

          npvRiskValue =
            npvAllocationQty != null ? npvRiskValue.plus(npvAllocationQty) : npvRiskValue;
          npvCounterValue =
            npvSettleQty != null ? npvCounterValue.plus(npvSettleQty) : npvCounterValue;
          npvFilledBaseValue =
            npvFillValue != null ? npvFilledBaseValue.plus(npvFillValue) : npvFilledBaseValue;
        } else {
          sellValue++;
          riskValue = allocationQty != null ? riskValue.minus(allocationQty) : riskValue;
          counterValue =
            allocCounterAmt != null ? counterValue.minus(allocCounterAmt) : counterValue;
          filledBaseValue = fillValue != null ? filledBaseValue.minus(fillValue) : filledBaseValue;

          npvRiskValue =
            npvAllocationQty != null ? npvRiskValue.minus(npvAllocationQty) : npvRiskValue;
          npvCounterValue =
            npvSettleQty != null ? npvCounterValue.minus(npvSettleQty) : npvCounterValue;
          npvFilledBaseValue =
            npvFillValue != null ? npvFilledBaseValue.minus(npvFillValue) : npvFilledBaseValue;
        }

        if (
          riskAggregate.isSwap &&
          (CubiqTS.AllocationType.PRIME == allocation.allocationType ||
            CubiqTS.AllocationType.CROSS == allocation.allocationType ||
            CubiqTS.AllocationType.COMPONENT == allocation.allocationType)
        ) {
          if (null == riskAggregate.swapNearAlloc) {
            riskAggregate.swapNearAlloc = allocation;
          } else if (allocation.settleDate.isBefore(riskAggregate.swapNearAlloc.settleDate)) {
            riskAggregate.swapFarAlloc = riskAggregate.swapNearAlloc;
            riskAggregate.swapNearAlloc = allocation;
          } else {
            riskAggregate.swapFarAlloc = allocation;
          }
        }
      }
    }

    if (riskValue.isLessThanOrEqualTo(ZERO)) {
      riskAggregate.clientIntent = CubiqTS.OrderIntent.SELL;
      riskAggregate.riskAmount = riskValue.negated();
      // this.usdAmount = usdValue.negated();
      riskAggregate.counterAmount = counterValue.negated();
    } else {
      riskAggregate.clientIntent = CubiqTS.OrderIntent.BUY;
      riskAggregate.riskAmount = riskValue;
      // this.usdAmount = usdValue;
      riskAggregate.counterAmount = counterValue;
    }

    if (npvRiskValue.isLessThanOrEqualTo(ZERO)) {
      riskAggregate.npvIntent = CubiqTS.OrderIntent.SELL;
      riskAggregate.npvRiskAmount = npvRiskValue.negated();
      riskAggregate.npvCounterAmount = npvCounterValue.negated();
    } else {
      riskAggregate.npvIntent = CubiqTS.OrderIntent.BUY;
      riskAggregate.npvRiskAmount = npvRiskValue;
      riskAggregate.npvCounterAmount = npvCounterValue;
    }

    riskAggregate.npvRiskAmountSigned = npvRiskValue;

    riskAggregate.usdAmount = usdValue;
    riskAggregate.filledBaseAmount = filledBaseValue;
    riskAggregate.npvFilledBaseAmount = npvFilledBaseValue;
    riskAggregate.buyCount = buyValue;
    riskAggregate.sellCount = sellValue;
  }
}
